import React from 'react';
import {Text, Toggle, WithTooltip, Skeleton} from '@albert-io/atomic';

import type {AdminSchool} from './useAdminSchools';
import {usePreviewLockImpactQuery} from './previewLockImpact.queries.generated';
import {GUIDE_LEVEL_LOCK_TYPE} from './manageRestrictionsModal.constants';

interface SchoolRestrictionRowProps {
  school: AdminSchool;
  guideLevelId?: string;
  isRestricted: boolean;
  onToggle: () => void;
}

// Per-school impact copy (Lucy, ManageRestrictions thread 2026-07-02): x = closed, y = open.
const getImpactMessage = (closedAssignments: number, openAssignments: number): string => {
  if (closedAssignments === 0 && openAssignments === 0) {
    return 'Restricting will not impact any active or closed assignments.';
  }
  if (closedAssignments > 0 && openAssignments > 0) {
    return `Restricting will block these questions in ${closedAssignments} closed and ${openAssignments} open assignments.`;
  }
  if (closedAssignments > 0) {
    return `Restricting will block these questions in ${closedAssignments} closed ${
      closedAssignments === 1 ? 'assignment' : 'assignments'
    }.`;
  }
  return `Restricting will block these questions in ${openAssignments} open ${
    openAssignments === 1 ? 'assignment' : 'assignments'
  }.`;
};

export const SchoolRestrictionRow = ({
  school,
  guideLevelId,
  isRestricted,
  onToggle
}: SchoolRestrictionRowProps) => {
  const {data, loading} = usePreviewLockImpactQuery({
    variables: {lockType: GUIDE_LEVEL_LOCK_TYPE, sourceId: guideLevelId ?? '', schoolId: school.id},
    // No impact preview for non-Pro schools — restriction is disabled for them.
    skip: !guideLevelId || !school.hasPro
  });
  const impact = data?.previewLockImpact;
  // Shown regardless of restrict state (per the Figma annotation); getImpactMessage owns the
  // no-impact copy too, so this is null only until the preview has loaded.
  const displayMessage = impact
    ? getImpactMessage(impact.closedAssignmentCount, impact.openAssignmentCount)
    : null;

  return (
    <tr className='border-b border-border-subtle last:border-b-0'>
      <td className='w-full px-x2 py-x1-half'>
        <Text as='div'>{school.name}</Text>
        {/* Reserve the impact line with a skeleton while in flight so the row height doesn't jump. */}
        <div className='mt-x0-quarter' aria-busy={loading}>
          {loading ? (
            <>
              <Skeleton className='h-x1 rounded-x1' />
              <span className='sr-only'>Loading assignment impact</span>
            </>
          ) : (
            displayMessage && (
              <Text as='div' size='s' color='tertiary'>
                {displayMessage}
              </Text>
            )
          )}
        </div>
      </td>
      <td className='border-l border-border-primary px-x2 py-x1-half text-left align-middle whitespace-nowrap'>
        {school.hasPro ? (
          <Toggle
            checked={isRestricted}
            onChange={onToggle}
            aria-label={`Restrict ${school.name}`}
          />
        ) : (
          <WithTooltip content='Cannot restrict at non pro school'>
            <Toggle checked={isRestricted} disabled aria-label={`Restrict ${school.name}`} />
          </WithTooltip>
        )}
      </td>
    </tr>
  );
};

export default SchoolRestrictionRow;
